import { NEXT_API_DEV_LOGS_PATH } from "@/api/nextApiPaths";

import { isLoggerRateLimitDisabled } from "./runtimeConfig";
import { sanitizeLogData } from "./sanitize";
import { ConsoleTransport } from "./transports/console";
import { RemoteTransport } from "./transports/remote";
import { ILogger, ITransport, LogContext, LogLevel, LogMessage } from "./types";

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 4
};

const RATE_LIMIT = {
  MAX_LOG_SIZE: 100 * 1024, // 100KB
  MAX_REQUESTS_PER_MINUTE: 1000,
  RESET_INTERVAL_MS: 60 * 1000 // 1 minute
};

const PROJECT_PATH_MARKERS = [
  "/src/",
  "/pages/",
  "/components/",
  "/utils/",
  "/hooks/",
  "/configs/",
  "/public/",
  "/app/"
];

function toProjectRelativePath(fullPath: string): string {
  for (const marker of PROJECT_PATH_MARKERS) {
    const idx = fullPath.indexOf(marker);
    if (idx !== -1) {
      return fullPath.slice(idx);
    }
  }
  return fullPath;
}

function cleanFilePath(filePath: string): string {
  if (!filePath) return "unknown";

  // Handle webpack paths
  if (filePath.includes("webpack-internal:///")) {
    return filePath
      .replace("webpack-internal:///(pages-dir-node)/", "")
      .replace("webpack-internal:///(rsc)/", "")
      .replace("webpack-internal:///", "");
  }

  // Remove query parameters
  filePath = filePath.split("?")[0];

  // Handle filesystem paths
  const projectRoot = typeof process !== "undefined" && process.cwd ? process.cwd() : "";
  if (projectRoot && filePath.startsWith(projectRoot)) {
    return filePath.substring(projectRoot.length + 1);
  }

  return filePath;
}

function getCallerLocation(): { file?: string; line?: number; column?: number } {
  try {
    const error = new Error();
    const stack = error.stack?.split("\n") || [];

    // Find the first stack frame that's not from the logger
    const callerLine =
      stack.find(
        (line) =>
          line !== "Error" &&
          !line.includes("at getCallerLocation ") &&
          !line.includes("at logger.") &&
          !line.includes("at Logger.") &&
          !line.includes("node_modules")
      ) || "";

    // Match file path and line numbers
    const match =
      callerLine.match(/\(? + at +(.+?) \(.+?:(\d+):(\d+)/) ||
      callerLine.match(/\(?(.+?):(\d+):(\d+)/) ||
      callerLine.match(/(\S+):(\d+):(\d+)/);

    if (match) {
      let filePath = match[1];

      // Clean file path (remove webpack prefixes, query params, etc.)
      filePath = cleanFilePath(filePath);

      // Convert to project-relative path
      filePath = toProjectRelativePath(filePath);

      return {
        file: filePath
        // line: parseInt(match[2]),
        // column: parseInt(match[3])
      };
    }
  } catch (e) {
    console.error("Error getting caller location:", e);
  }
  return {};
}

let globalLoggerContext: LogContext = {};

export function setLoggerContext(context: LogContext): void {
  globalLoggerContext = context || {};
}

export class Logger implements ILogger {
  private static instance: Logger;
  private context: LogContext = {};
  private transports: ITransport[] = [new ConsoleTransport()];
  private level: LogLevel = "debug";
  private requestCount = 0;
  private lastResetTime = Date.now();

  private constructor() {
    this.level = (process.env.NEXT_PUBLIC_LOG_LEVEL as LogLevel) || "debug";
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  setContext(context: LogContext): void {
    this.context = { ...this.context, ...context };
  }

  private checkRateLimit(): boolean {
    if (isLoggerRateLimitDisabled()) return true;

    const now = Date.now();
    if (now - this.lastResetTime > RATE_LIMIT.RESET_INTERVAL_MS) {
      this.requestCount = 0;
      this.lastResetTime = now;
    }

    if (this.requestCount >= RATE_LIMIT.MAX_REQUESTS_PER_MINUTE) {
      return false;
    }

    this.requestCount++;
    return true;
  }

  child(context: LogContext): ILogger {
    const child = new (this.constructor as new () => Logger)();
    child.transports = this.transports;
    child.level = this.level;
    child.setContext({ ...this.context, ...context });
    return child;
  }

  debug(...args: unknown[]): void {
    this._logWithFlexibleArgs("debug", ...args);
  }

  info(...args: unknown[]): void {
    this._logWithFlexibleArgs("info", ...args);
  }

  warn(...args: unknown[]): void {
    this._logWithFlexibleArgs("warn", ...args);
  }

  error(...args: unknown[]): void {
    this._logWithFlexibleArgs("error", ...args);
  }

  /**
   * Flexible argument handler for logger methods.
   * Supports:
   *   logger.error("msg", errorObj)
   *   logger.error(errorObj, { context })
   *   logger.error("msg", { context })
   *   logger.error("msg", errorObj, { context })
   */
  private _logWithFlexibleArgs(level: LogLevel, ...args: unknown[]): void {
    let message: string = "";
    let errorObj: Error | undefined;
    let context: LogContext = {};

    if (args.length === 0) return;

    // Parse arguments
    if (typeof args[0] === "string") {
      message = args[0] as string;
      if (args.length > 1) {
        if (args[1] instanceof Error) {
          errorObj = args[1] as Error;
          if (args.length > 2 && typeof args[2] === "object" && args[2] !== null) {
            context = args[2] as LogContext;
          }
        } else if (typeof args[1] === "object" && args[1] !== null) {
          context = args[1] as LogContext;
        }
      }
    } else if (args[0] instanceof Error) {
      errorObj = args[0] as Error;
      message = errorObj.message;
      if (args.length > 1 && typeof args[1] === "object" && args[1] !== null) {
        context = args[1] as LogContext;
      }
    }

    // If errorObj exists, add its stack to context
    if (errorObj) {
      context = { ...context, error: errorObj.stack || errorObj.toString() };
    }

    this.log(level, message, context);
  }

  isLevelEnabled(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.level];
  }

  private log(level: LogLevel, message: string, context: LogContext): void {
    // Suppress all logs in test environment
    if (process.env.NODE_ENV === "test") return;

    if (!this.isLevelEnabled(level) || !this.checkRateLimit()) return;

    const timestamp = new Date().toISOString();
    const sanitizedContext = sanitizeLogData(context);

    if (level === "error") {
      this.trackError(level, message, context);
    }

    const logEntry: LogMessage = {
      timestamp,
      level,
      message,
      ...getCallerLocation(),
      ...sanitizeLogData(globalLoggerContext),
      ...sanitizeLogData(this.context),
      ...sanitizedContext
    };

    this.transports.forEach((transport) => {
      try {
        transport.log(logEntry);
      } catch (err) {
        console.error("Error in logger transport:", err);
      }
    });
  }

  addTransport(transport: ITransport): void {
    this.transports.push(transport);
  }

  removeTransport(transport: ITransport): void {
    this.transports = this.transports.filter((t) => t !== transport);
  }

  // Add to Logger class
  // Sentry integration: add type for window.Sentry and only call if available
  private trackError(level: LogLevel, message: string, context: LogContext): void {
    // Only process error level logs
    if (level !== "error") return;

    // Check if we're in a browser environment
    if (typeof window === "undefined") return;

    // TypeScript: declare Sentry on window if present
    type SentryType = {
      captureException: (error: unknown, options?: { extra?: unknown }) => void;
      captureMessage: (message: string, options?: { level?: string; extra?: unknown }) => void;
    };
    const win = window as typeof window & { Sentry?: SentryType };

    if (win.Sentry) {
      // Always pass message as string, error stack is in context if present
      win.Sentry.captureMessage(message, {
        level: "error",
        extra: context
      });
    }
  }
}

export const logger = Logger.getInstance();

if (typeof window !== "undefined") {
  //   // Add file logging in development
  //   if (process.env.NODE_ENV === "development") {
  //     const fileTransport = new FileTransport();
  //     fileTransport.initialize().catch(console.error);
  //     logger.addTransport(fileTransport);
  //   }

  // Add remote logging in development
  if (process.env.NODE_ENV === "development") {
    logger.addTransport(
      new RemoteTransport(NEXT_API_DEV_LOGS_PATH, {
        suppressBootstrapNoise: true,
        disableRateLimit: isLoggerRateLimitDisabled()
      })
    );
  }

  // Add remote logging in production
  if (process.env.NODE_ENV === "production" && process.env.NEXT_PUBLIC_LOG_ENDPOINT) {
    logger.addTransport(
      new RemoteTransport(process.env.NEXT_PUBLIC_LOG_ENDPOINT, {
        disableRateLimit: isLoggerRateLimitDisabled()
      })
    );
  }
}
